/* eslint-disable jsx-a11y/label-has-associated-control */
import { Button, CircularProgress, Dialog, IconButton } from '@material-ui/core';
import { XIcon } from '@phosphor-icons/react';
import { FC, useEffect, useState } from 'react';
import SearchAutocomplete from '../../shared/components/SearchAutocomplete';
import { ConsultantSummary } from '../../shared/types';
import { BORDER, createAdminPageStyles, MUTED, NAVY, TEXT } from '../../style';
import {
  REASSIGN_BENEFICIARIES_EMPTY,
  REASSIGN_BENEFICIARIES_HINT,
  REASSIGN_BENEFICIARIES_LABEL,
  REASSIGN_BENEFICIARIES_TITLE,
} from '../constants';

interface ReassignBeneficiariesModalProps {
  open: boolean;
  loading?: boolean;
  saving?: boolean;
  consultants: ConsultantSummary[];
  title?: string;
  fieldLabel?: string;
  hint?: string;
  emptyText?: string;
  onClose: () => void;
  onSave: (consultantId: number) => void;
}

const consultantLabel = (consultant: ConsultantSummary) =>
  `${consultant.firstname || ''} ${consultant.lastname || ''}`.trim();

const useStyles = createAdminPageStyles({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 560,
      width: '100%',
      margin: 16,
      padding: '28px 32px 24px',
      boxShadow: '0 16px 40px rgba(14, 18, 71, 0.18)',
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontSize: 20,
    fontWeight: 600,
    color: '#000B33',
    margin: 0,
    lineHeight: 1.3,
  },
  dialogClose: {
    margin: '-8px -8px 0 0',
    color: TEXT,
    padding: 6,
  },
  field: {
    marginBottom: 12,
  },
  hint: {
    fontFamily: 'Poppins',
    fontSize: 13,
    fontWeight: 400,
    color: MUTED,
    lineHeight: 1.45,
    margin: '0 0 24px',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 0 32px',
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  cancelBtn: {
    background: '#FFFFFF',
    color: NAVY,
    border: `1.5px solid ${BORDER}`,
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: 'none',
    '&:hover': {
      background: '#F7F8FB',
      boxShadow: 'none',
    },
    '&:disabled': {
      color: MUTED,
    },
  },
  confirmBtn: {
    background: '#000A8D',
    color: '#FFFFFF',
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: 'none',
    '&:hover': {
      background: '#1b2158',
      boxShadow: 'none',
    },
    '&:disabled': {
      background: '#E8EAF0',
      color: MUTED,
    },
  },
});

const ReassignBeneficiariesModal: FC<ReassignBeneficiariesModalProps> = ({
  open,
  loading = false,
  saving = false,
  consultants,
  title = REASSIGN_BENEFICIARIES_TITLE,
  fieldLabel = REASSIGN_BENEFICIARIES_LABEL,
  hint = REASSIGN_BENEFICIARIES_HINT,
  emptyText = REASSIGN_BENEFICIARIES_EMPTY,
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [draftId, setDraftId] = useState<number | ''>('');

  useEffect(() => {
    if (open) {
      setDraftId('');
    }
  }, [open]);

  const selectedConsultant =
    consultants.find(item => Number(item.id) === Number(draftId)) || null;

  const handleSave = () => {
    if (!draftId || saving || loading) {
      return;
    }
    onSave(Number(draftId));
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialog}
      aria-labelledby="reassign-beneficiaries-title">
      <div className={classes.dialogHeader}>
        <h2 id="reassign-beneficiaries-title" className={classes.dialogTitle}>
          {title}
        </h2>
        <IconButton
          aria-label="Fermer"
          className={classes.dialogClose}
          onClick={onClose}
          disabled={saving}>
          <XIcon size={20} />
        </IconButton>
      </div>

      {loading ? (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className={classes.field}>
            <label className={classes.label} htmlFor="reassign-consultant-select">
              {fieldLabel}
            </label>
            <SearchAutocomplete
              id="reassign-consultant-select"
              options={consultants}
              value={selectedConsultant}
              placeholder="Rechercher un nom ou un prénom"
              getOptionLabel={consultantLabel}
              noOptionsText={emptyText}
              disabled={saving}
              onChange={selected => {
                const consultant = selected as ConsultantSummary | null;
                setDraftId(consultant ? Number(consultant.id) : '');
              }}
            />
          </div>
          {hint ? <p className={classes.hint}>{hint}</p> : null}
        </>
      )}

      <div className={classes.actions}>
        <Button className={classes.cancelBtn} onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          className={classes.confirmBtn}
          onClick={handleSave}
          disabled={loading || saving || !draftId}>
          {saving ? 'Chargement...' : 'Enregistrer'}
        </Button>
      </div>
    </Dialog>
  );
};

export default ReassignBeneficiariesModal;
